/**
 * One lesson, and everything it produced.
 *
 * The unit of a learner's record is not "a video" and, elsewhere, "a score" —
 * it is the lesson: what you watched, how many times you came back, and how the
 * check attached to it went. Watches and checks live in the same card, on the
 * same timeline, because that is the only arrangement in which the numbers
 * explain each other: a 40% check under two full watches says something a
 * scoreboard never could.
 *
 * Collapsed it reads as a single line of standing. Expanded it shows every
 * instance, because "you watched this three times" is a claim, and the three
 * dates are the evidence.
 */

import { useState } from 'react';
import { Play, GraduationCap, ChevronDown, Clock, RotateCcw } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { cn } from '../../lib/utils';
import { subjectMeta, glassPanel, inkMuted } from './learnShared';
import { formatRelativeDate } from './useLearnerProgress';
import type { JourneyLesson } from '../../services/learnerAPI';

const fmtMs = (ms: number | null): string => {
  if (!ms) return '—';
  const m = Math.round(ms / 60000);
  if (m < 1) return 'under a minute';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} hr ${m % 60} min`;
};

const scoreTone = (pct: number | null) =>
  pct == null
    ? 'bg-[#191831]/[0.06] text-[#575277] dark:bg-white/10 dark:text-[#A8A5C4]'
    : pct >= 70
      ? 'bg-[#17B08B]/[0.14] text-[#0E8A6B] dark:text-[#3DDBB0]'
      : 'bg-[#B45309]/[0.14] text-[#96450A] dark:text-[#E8A85C]';

export function JourneyCard({ lesson }: { lesson: JourneyLesson }) {
  const [open, setOpen] = useState(false);
  const meta = subjectMeta(lesson.subject);

  const watches = lesson.watches;
  const checks = lesson.checks;
  const bestWatch = watches.reduce((m, w) => Math.max(m, w.maxPercent), 0);
  const totalWatchMs = watches.reduce((s, w) => s + w.watchTimeMs, 0);
  const finished = checks.filter((c) => c.finished);
  const bestScore = finished.reduce<number | null>(
    (b, c) => (c.percent != null && (b == null || c.percent > b) ? c.percent : b),
    null,
  );
  const lastTouch =
    [...watches.map((w) => w.startedAt), ...checks.map((c) => c.startedAt)]
      .filter(Boolean)
      .sort()
      .pop() ?? null;

  const hasDetail = watches.length + checks.length > 0;

  return (
    <div className={cn(glassPanel, 'overflow-hidden')}>
      <div className="flex items-stretch gap-0">
        {/* The lesson's own frame, so the record reads as the library it came from. */}
        <div className="relative hidden w-[150px] shrink-0 overflow-hidden bg-[#101018] sm:block">
          {lesson.thumbnailUrl ? (
            <img src={lesson.thumbnailUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full" style={{ background: meta.gradient }} />
          )}
        </div>

        <div className="min-w-0 flex-1 p-4 sm:p-5">
          <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em]">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: meta.color }} />
            <span style={{ color: meta.color }} className="dark:brightness-[1.6]">
              {lesson.subject ?? 'Lesson'}
            </span>
            {lesson.grade && <span className={cn('font-semibold tracking-normal', inkMuted)}>· {lesson.grade}</span>}
          </div>

          <Link
            to="/learn/$shareToken"
            params={{ shareToken: lesson.shareToken }}
            className="mt-1 block font-serif text-[20px] leading-[1.28] tracking-tight hover:text-[#5B2EE5] dark:hover:text-[#BFA8FA]"
          >
            {lesson.title ?? 'Untitled lesson'}
          </Link>

          {/* Standing: watches on the left, checks on the right, always both —
              a lesson with no check says so rather than leaving a gap. */}
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12.5px]">
            <span className="inline-flex items-center gap-1.5">
              <Play className="h-3.5 w-3.5" style={{ color: meta.color }} />
              <b className="tabular-nums">{watches.length}</b>
              <span className={inkMuted}>{watches.length === 1 ? 'watch' : 'watches'}</span>
              {bestWatch > 0 && <span className={inkMuted}>· {bestWatch}% furthest</span>}
            </span>

            <span className="inline-flex items-center gap-1.5">
              <GraduationCap className="h-3.5 w-3.5" style={{ color: meta.color }} />
              {finished.length > 0 ? (
                <>
                  <b className="tabular-nums">{finished.length}</b>
                  <span className={inkMuted}>{finished.length === 1 ? 'check' : 'checks'}</span>
                  {bestScore != null && (
                    <span className={cn('rounded-full px-2 py-0.5 text-[11.5px] font-bold tabular-nums', scoreTone(bestScore))}>
                      best {bestScore}%
                    </span>
                  )}
                </>
              ) : (
                <span className={inkMuted}>{checks.length ? 'check started, not finished' : 'no check taken'}</span>
              )}
            </span>

            {totalWatchMs > 0 && (
              <span className={cn('inline-flex items-center gap-1.5', inkMuted)}>
                <Clock className="h-3.5 w-3.5" /> {fmtMs(totalWatchMs)}
              </span>
            )}
            {lastTouch && <span className={inkMuted}>{formatRelativeDate(lastTouch)}</span>}
          </div>
        </div>

        {hasDetail && (
          <button
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? 'Hide every attempt' : 'Show every attempt'}
            className={cn(
              'flex w-11 shrink-0 items-center justify-center border-l border-black/[0.06] transition-colors hover:bg-white/60 dark:border-white/10 dark:hover:bg-white/[0.06]',
              inkMuted,
            )}
          >
            <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
          </button>
        )}
      </div>

      {open && (
        <div className="grid gap-5 border-t border-black/[0.06] p-4 sm:grid-cols-2 sm:p-5 dark:border-white/10">
          <Instances
            title={watches.length === 1 ? 'The watch' : `Every watch · ${watches.length}`}
            empty="Not watched yet."
            rows={watches.map((w) => ({
              key: w.viewId,
              when: w.startedAt,
              primary: `${w.maxPercent}% watched`,
              secondary: fmtMs(w.watchTimeMs),
              done: w.completed,
              hue: meta.color,
            }))}
          />
          <Instances
            title={checks.length === 1 ? 'The check' : `Every check · ${checks.length}`}
            empty="No check taken for this lesson."
            rows={checks.map((c) => ({
              key: c.viewId,
              when: c.startedAt,
              primary: c.finished
                ? c.percent != null
                  ? `${c.percent}%`
                  : 'Completed'
                : 'Left part-way',
              secondary:
                c.correct != null && c.total != null
                  ? `${c.correct} of ${c.total} first time${c.durationMs ? ` · ${fmtMs(c.durationMs)}` : ''}`
                  : c.durationMs
                    ? fmtMs(c.durationMs)
                    : '',
              done: c.finished,
              tone: c.finished ? scoreTone(c.percent) : undefined,
              hue: meta.color,
            }))}
          />
        </div>
      )}
    </div>
  );
}

interface Row {
  key: string;
  when: string | null;
  primary: string;
  secondary: string;
  done: boolean;
  tone?: string;
  hue: string;
}

function Instances({ title, rows, empty }: { title: string; rows: Row[]; empty: string }) {
  return (
    <div>
      <p className={cn('text-[11px] font-bold uppercase tracking-[0.12em]', inkMuted)}>{title}</p>
      {rows.length === 0 ? (
        <p className={cn('mt-2 text-[13px]', inkMuted)}>{empty}</p>
      ) : (
        <ol className="mt-2.5 flex flex-col">
          {rows.map((r, i) => (
            <li key={r.key} className="relative flex gap-3 pb-3 last:pb-0">
              {/* A spine, so repeated attempts read as a sequence in time rather
                  than an unordered pile of numbers. */}
              <span className="relative flex w-3 shrink-0 justify-center">
                <span
                  aria-hidden="true"
                  className={cn('absolute top-1.5 h-2 w-2 rounded-full', !r.done && 'ring-2 ring-white dark:ring-[#181A2A]')}
                  style={{ background: r.done ? r.hue : 'transparent', boxShadow: r.done ? 'none' : `inset 0 0 0 2px ${r.hue}` }}
                />
                {i < rows.length - 1 && (
                  <span aria-hidden="true" className="absolute top-4 h-full w-px bg-black/10 dark:bg-white/15" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span
                    className={cn(
                      'text-[13.5px] font-bold tabular-nums',
                      r.tone && 'rounded-full px-2 py-0.5 text-[12px]',
                      r.tone,
                    )}
                  >
                    {r.primary}
                  </span>
                  {r.when && <span className={cn('text-[12px]', inkMuted)}>{formatRelativeDate(r.when)}</span>}
                </div>
                {r.secondary && <p className={cn('text-[12px]', inkMuted)}>{r.secondary}</p>}
              </div>
              {r.done && i === 0 && rows.length > 1 && (
                <RotateCcw className={cn('h-3.5 w-3.5 shrink-0', inkMuted)} aria-label="most recent" />
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export { fmtMs as formatDurationMs };
